import { useCallback, useEffect, useState } from 'react';
import { appeler, ErreurApi, type Profil } from './api';
import { ouvrirSessionCourrier, didConnu } from '../../src/partage/courrier-cle-agent.js';

/**
 * D.432 — MOWIBO COURRIER, ÉCRAN DE L'AGENT DANS MOWIBO CADASTRAL.
 *
 * ⚠️ COURRIER N'EST PAS UNE APPLICATION, C'EST UN MODULE DE CELLE-CI. Un agent
 * du cadastre travaille dans Mowibo Cadastral et n'en sort jamais pour écrire :
 * le point d'entrée reste le dossier, et cet écran est la vue transverse. C'est
 * pourquoi le rail latéral ne propose pas « Cadastral » comme destination — on
 * y est déjà. Même règle que Mowibo OS, où tout module vit dans l'OS.
 *
 * ⚠️ CET ÉCRAN A UNE SEULE OBLIGATION QUI PRIME LES AUTRES, et c'est celle de
 * `fiscal.tsx` appliquée aux actes : ne jamais laisser lire « aucun acte » là où
 * la vérité est « le rail n'a pas répondu », ni « vous n'avez rien » là où elle
 * est « vous n'avez pas encore de session ». Sur une liste vide, les trois se
 * ressemblent. Quatre états distincts sont donc nommés et ne se replient jamais
 * l'un sur l'autre : module FERMÉ · session ABSENTE · rail MUET · liste VIDE.
 *
 * ⚠️ TOUTES LES PHRASES D'ÉTAT VIENNENT DU SERVICE. L'écran ne les compose pas.
 * `GET /courrier/etat` rend un `message` qui explique pourquoi le module est
 * fermé — deux variables d'environnement manquantes, pas une panne. Un écran qui
 * réécrirait cette phrase ferait ouvrir un incident pour un défaut de réglage.
 *
 * ⚠️ CE QUE CET ÉCRAN NE FAIT PAS, ET QUI EST DIT À L'ÉCRAN PLUTÔT QUE TU :
 *   — DÉPOSER un acte. Le dépôt part du dossier, se signe, et scelle ; il
 *     appartient au parcours du dossier, pas à une vue transverse.
 *   — ACCUSER réception. L'accusé est un geste de DESTINATAIRE : il date
 *     l'arrivée du dossier et fait partir ses délais. Il vit dans la boîte du
 *     destinataire, pas dans cette vue transverse.
 *
 *     ⚠️⚠️ CORRIGÉ LE 23-09-2026, ET L'ERREUR VAUT D'ÊTRE GARDÉE. Ce bloc
 *     annonçait que `/accuser` produirait des « 403 non_destinataire » chez un
 *     agent non destinataire. `grep -rn non_destinataire src` rendait **0**
 *     (témoins positifs : `vers_mode` 15, `exiger` 289 — la recherche voyait).
 *     La garde décrite N'EXISTAIT PAS : la route accusait en réalité TOUTES les
 *     transmissions en attente de l'affaire, pour n'importe quel compte portant
 *     `fon.affaire:traiter`, y compris celles adressées à un autre service.
 *     Une garde imaginaire coûte deux fois — elle ne protège pas, et elle fait
 *     renoncer à ce qu'elle était censée rendre possible : c'est sur cette
 *     croyance qu'aucun bouton n'a été posé pendant onze jours.
 *     La garde existe désormais en BASE (`trg_accuse_est_du_destinataire`,
 *     migration 224), donc hors de portée d'un appelant qui contournerait la
 *     route, et la route n'accuse plus qu'UNE transmission — la plus ancienne
 *     qui soit adressée à l'appelant.
 */

interface EtatModule { configure: boolean; administration: string | null; message: string }
interface Canal { id: string; nature: string; name: string | null; classification: string; conservation_ans: number; status: string; sealed_at: string | null; created_at: string; actes: string | number }
interface Acte { id: string; reference: string; classification: string; conservation_ans: number; etat: string; worm_sequence: number | null; scelle_at: string | null; created_at: string; fonction: string }

/** Le jeton de session Courrier ne quitte pas cet onglet : ni cookie, ni stockage. */
let jetonCourrier: string | null = null;

/**
 * ⚠️ LE JETON VOYAGE DANS UN EN-TÊTE DÉDIÉ, PAS DANS `Authorization`.
 * `Authorization` porte déjà l'authentification Cadastral de l'agent ; y glisser
 * le jeton du rail écraserait l'une des deux. Le périmètre relaie
 * `x-courrier-session` vers Connect, sans le stocker : c'est Connect qui décide
 * ce que CET agent peut voir, pas Cadastral.
 */
function lireRail<T>(chemin: string): Promise<T> {
  return appeler<T>(chemin, {
    headers: jetonCourrier ? { 'x-courrier-session': jetonCourrier } : {},
  });
}

function messageDe(e: unknown): string {
  if (e instanceof ErreurApi) return e.message;
  return String((e as Error)?.message ?? e);
}

export function Courrier({ profil }: { profil: Profil }) {
  const [etat, setEtat] = useState<EtatModule | null>(null);
  const [did, setDid] = useState<string | null>(null);
  const [session, setSession] = useState<{ expire_dans_s?: number } | null>(null);
  const [canaux, setCanaux] = useState<Canal[] | null>(null);
  const [canalOuvert, setCanalOuvert] = useState<string | null>(null);
  const [actes, setActes] = useState<Acte[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [travail, setTravail] = useState<string | null>('Lecture de l’état du module…');

  const chargerEtat = useCallback(async () => {
    setTravail('Lecture de l’état du module…'); setErreur(null);
    try {
      setEtat(await lireRail<EtatModule>('/courrier/etat'));
      // Une clé déjà posée sur CET appareil : on le dit avant toute action, pour
      // qu'un agent sache s'il s'apprête à en créer une ou à réutiliser la sienne.
      try { setDid(await didConnu()); } catch { setDid(null); }
    } catch (e) { setErreur(messageDe(e)); }
    finally { setTravail(null); }
  }, []);
  useEffect(() => { void chargerEtat(); }, [chargerEtat]);

  const ouvrirSession = async () => {
    setErreur(null); setTravail('Ouverture de la session — signature du défi…');
    try {
      const r = await ouvrirSessionCourrier({
        externalId: profil.compteId,
        baseModule: '/courrier/agents',
        // On injecte NOTRE émetteur : il porte `credentials: 'same-origin'`, donc
        // le cookie httpOnly de Cadastral, sans lequel `exiger()` refuserait.
        appeler: (chemin: string, corps: unknown) => appeler(chemin, { method: 'POST', body: JSON.stringify(corps) }),
      }) as { access_token?: string; expire_dans_s?: number };
      if (!r?.access_token) throw new Error('Le rail a répondu sans jeton de session : rien n’est ouvert.');
      jetonCourrier = r.access_token;
      setSession({ expire_dans_s: r.expire_dans_s });
      setDid(await didConnu());
      await chargerCanaux();
    } catch (e) { setErreur(messageDe(e)); }
    finally { setTravail(null); }
  };

  const chargerCanaux = async () => {
    setTravail('Lecture de vos fils institutionnels…'); setErreur(null);
    try { setCanaux((await lireRail<{ canaux: Canal[] }>('/courrier/canaux')).canaux ?? []); }
    catch (e) { setCanaux(null); setErreur(messageDe(e)); }
    finally { setTravail(null); }
  };

  const ouvrirCanal = async (id: string) => {
    setCanalOuvert(id); setActes(null); setErreur(null); setTravail('Lecture des actes…');
    try { setActes((await lireRail<{ actes: Acte[] }>(`/courrier/canaux/${id}/actes`)).actes ?? []); }
    catch (e) { setActes(null); setErreur(messageDe(e)); }
    finally { setTravail(null); }
  };

  return (
    <div className="ecran ecran-courrier">
      <header className="ecran-tete">
        <h2>Courrier</h2>
        <p className="sous-titre">
          Actes officiels adressés à une identité vérifiée. Le contenu reste chiffré
          de bout en bout&nbsp;: ce module en montre l’enveloppe, jamais le texte.
        </p>
      </header>

      {/* ─── 1. Le module est-il ouvert ? « Fermé » n’est pas « cassé ». ─── */}
      {etat && (
        <section className={`carte ${etat.configure ? 'carte-ok' : 'carte-attente'}`}>
          <h3>{etat.configure ? 'Module ouvert' : 'Module fermé'}</h3>
          {/* La phrase vient du SERVICE, telle quelle. */}
          <p>{etat.message}</p>
          {etat.administration && <p className="discret">Administration&nbsp;: {etat.administration}</p>}
        </section>
      )}

      {/* ─── 2. La clé de cet appareil ─────────────────────────────────── */}
      {etat?.configure && (
        <section className="carte">
          <h3>Votre clé de signature</h3>
          {did ? (
            <p className="discret">Cet appareil porte déjà votre clé&nbsp;: <code>{did.slice(0, 28)}…</code></p>
          ) : (
            <p className="discret">Aucune clé sur cet appareil&nbsp;: la première ouverture de session en créera une.</p>
          )}
          {/* ⚠️ À DIRE, PAS À TAIRE : la clé privée est non extractible. Elle est
              liée à CET appareil et CE navigateur, et ne peut pas être sauvegardée.
              Un agent qui vide les données du site la perd — la réponse est une
              rotation signée par l’ancienne, jamais une sauvegarde, sinon toute la
              garantie de non-répudiation tombe. */}
          <p className="avertissement">
            Cette clé ne quitte jamais cet appareil et ne peut pas être sauvegardée.
            Si vous effacez les données du site, elle est perdue&nbsp;: demandez alors
            une rotation à votre administration — elle se signe avec l’ancienne.
          </p>
          {!session && (
            <button className="bouton-principal" onClick={() => void ouvrirSession()} disabled={Boolean(travail)}>
              Ouvrir ma session Courrier
            </button>
          )}
          {session && (
            <p className="discret">
              Session ouverte{typeof session.expire_dans_s === 'number' ? ` — elle expire dans ${Math.round(session.expire_dans_s / 60)} min` : ''}.
            </p>
          )}
        </section>
      )}

      {travail && <p className="discret" role="status">{travail}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {/* ─── 3. Les fils — et les QUATRE états qui ne se confondent pas ─── */}
      {etat?.configure && !session && !travail && (
        <section className="carte carte-attente">
          <h3>Aucune session</h3>
          <p>
            Vos fils ne sont pas lus parce que vous n’avez pas encore de session Courrier,
            et non parce que vous n’en avez aucun. Ouvrez-en une ci-dessus.
          </p>
        </section>
      )}

      {session && canaux !== null && (
        <section className="carte">
          <h3>Vos fils institutionnels ({canaux.length})</h3>
          {canaux.length === 0 ? (
            <p>
              Le rail a répondu, et il ne vous ouvre aucun fil. Ce n’est pas une panne&nbsp;:
              un fil s’ouvre vers une identité vérifiée, depuis un dossier.
            </p>
          ) : (
            <table className="tableau">
              <thead>
                <tr><th>Fil</th><th>Nature</th><th>Classification</th><th>Conservation</th><th>Actes</th><th>Scellé</th></tr>
              </thead>
              <tbody>
                {canaux.map((c) => (
                  <tr key={c.id} className={canalOuvert === c.id ? 'ligne-active' : ''}
                      onClick={() => void ouvrirCanal(c.id)} style={{ cursor: 'pointer' }}>
                    <td>{c.name ?? <span className="discret">sans intitulé</span>}</td>
                    <td>{c.nature}</td>
                    <td>{c.classification}</td>
                    <td>{c.conservation_ans} ans</td>
                    <td>{c.actes}</td>
                    <td>{c.sealed_at ? 'oui' : <span className="discret">non</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {canalOuvert && actes !== null && (
        <section className="carte">
          <h3>Actes du fil ({actes.length})</h3>
          {actes.length === 0 ? (
            <p>
              Ce fil ne porte aucun acte déposé. Un acte préparé mais jamais signé n’en
              est pas un&nbsp;: il ne figure dans aucune liste.
            </p>
          ) : (
            <table className="tableau">
              <thead>
                <tr><th>Référence</th><th>Sous la fonction</th><th>Classification</th><th>État</th><th>Sceau</th><th>Déposé</th></tr>
              </thead>
              <tbody>
                {actes.map((a) => (
                  <tr key={a.id}>
                    <td><code>{a.reference}</code></td>
                    <td>{a.fonction}</td>
                    <td>{a.classification}</td>
                    <td>{a.etat}</td>
                    <td>{a.worm_sequence ?? <span className="discret">—</span>}</td>
                    <td>{a.created_at ? new Date(a.created_at).toLocaleString('fr-FR') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* ─── 4. Ce que ce module ne fait pas — dit, pas tu ─────────────── */}
      <section className="carte carte-attente">
        <h3>Ce que cette vue ne fait pas</h3>
        <ul>
          <li>
            <strong>Déposer un acte.</strong> Le dépôt part du dossier, il se signe et il
            scelle&nbsp;: il appartient au parcours du dossier, pas à une vue transverse.
          </li>
          <li>
            <strong>Accuser réception.</strong> L’accusé est un geste de destinataire, pas
            d’agent&nbsp;: le rail résout l’identité qui accuse depuis le compte, jamais
            depuis ce qu’il déclare. Il se fait dans la boîte officielle de l’administré.
          </li>
        </ul>
      </section>
    </div>
  );
}
